import React, { useEffect, useRef, useState, useCallback } from 'react';
import { copilotService } from './CopilotService';
import GeneralUtils from '../../../utils/GeneralUtils';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';
import './Copilot.css';
import {Divider} from 'primereact/divider';
import {InputTextarea} from "primereact/inputtextarea";


// Yardımcı fonksiyonlar (top-level)
const nowSeconds = () => Math.floor(Date.now()/1000);

function extractTrackingId(token){
  try {
    if(!token) return null;
    const prefix = token.split(':')[0];
    const parts = prefix.split(';');
    for(const p of parts){ const [k,v] = p.split('='); if(k==='tid') return v; }
  } catch { /* ignore */ }
  return null;
}

const CODEX_MODEL_ID = 'codex';
const GPT_MODEL_ID = 'gpt-5.2'; // Yeni eklenen native chat modeli

const Copilot = () => {
  // --- Floating panel states ---
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelClosing, setPanelClosing] = useState(false);
  const [panelWidth, setPanelWidth] = useState(()=>{
    if(typeof window !== 'undefined'){
      const saved = parseInt(localStorage.getItem('copilotPanelWidth'),10);
      const maxViewport = Math.floor(window.innerWidth * 0.95);
      if(!isNaN(saved) && saved >= 320 && saved <= maxViewport) return saved;
    }
    return 480;
  });
  const resizingRef = useRef(null); // {startX, startW}

  const togglePanel = () => {
    if(panelOpen){
      setPanelClosing(true);
      setTimeout(()=>{ setPanelOpen(false); setPanelClosing(false); }, 180);
    } else {
      setPanelOpen(true);
    }
  };
  const closePanel = () => { if(panelOpen) togglePanel(); };

  // ESC ile kapatma
  useEffect(()=>{
    if(!panelOpen) return; const onKey = (e)=>{ if(e.key==='Escape') closePanel(); }; window.addEventListener('keydown',onKey); return ()=> window.removeEventListener('keydown',onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[panelOpen]);

  // Resize handlers
  const onMouseMove = useCallback((e)=>{
    if(!resizingRef.current) return;
    const dx = resizingRef.current.startX - e.clientX;
    let newW = resizingRef.current.startW + dx;
    const maxViewport = typeof window !== 'undefined' ? Math.floor(window.innerWidth * 0.95) : 1600;
    if(newW < 320) newW = 320; if(newW > maxViewport) newW = maxViewport;
    setPanelWidth(newW);
  },[]);
  const stopResize = useCallback(()=>{
    if(resizingRef.current){ resizingRef.current = null; }
    document.body.style.userSelect='';
    if(typeof window !== 'undefined') localStorage.setItem('copilotPanelWidth', String(panelWidth));
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', stopResize);
  },[onMouseMove, panelWidth]);
  const startResize = (e) => {
    e.preventDefault();
    resizingRef.current = { startX: e.clientX, startW: panelWidth };
    document.body.style.userSelect='none';
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', stopResize);
  };

  // --- Existing states & logic kept below (auth, chat, etc.) ---
  // Auth & device flow
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [deviceDialogVisible, setDeviceDialogVisible] = useState(false);
  const [deviceCodeData, setDeviceCodeData] = useState(null); // { device_code,user_code,verification_uri, interval }
  const [deviceFlowError, setDeviceFlowError] = useState(null);
  const [pollIntervalSec, setPollIntervalSec] = useState(5);
  const [pollAttempts, setPollAttempts] = useState(0);
  const MAX_POLL_ATTEMPTS = 120; // güvenlik için ~10 dk (varsayılan 5s interval ile)
  const pollTimerRef = useRef(null);

  // Copilot token
  const [copilotTokenInfo, setCopilotTokenInfo] = useState(null); // {token, expires_at, refresh_in}
  const [copilotLoading, setCopilotLoading] = useState(false);
  const [showCopilotToken, setShowCopilotToken] = useState(false);
  const tokenRefreshTimerRef = useRef(null);
  const [tokenCountdown, setTokenCountdown] = useState(null); // saniye

  // ---- Copilot Token ----
  const loadCopilotToken = useCallback(async () => {
    setCopilotLoading(true);
    try {
      const data = await copilotService.getCopilotToken();
      setCopilotTokenInfo(data);
      GeneralUtils.showNotification({ type:'SUCCESS', message:'Copilot token alındı.' });
    } catch(e){
      GeneralUtils.showNotification({ type:'ERROR', message:'Copilot token alınamadı: '+e.message });
    } finally { setCopilotLoading(false);} // eslint-disable-line react-hooks/exhaustive-deps
  }, []);

  // --- Token Auto Refresh (schedule) yeniden eklendi ---
  const scheduleCopilotTokenRefresh = useCallback((info) => {
    if(tokenRefreshTimerRef.current){ clearTimeout(tokenRefreshTimerRef.current); tokenRefreshTimerRef.current=null; }
    if(!info) return;
    const { refresh_in, expires_at } = info;
    if(!refresh_in) return;
    const safety = 10; // saniye erken yenile
    const now = nowSeconds();
    let delaySec = refresh_in - safety;
    if(expires_at && expires_at < now + safety) delaySec = 2;
    if(delaySec < 1) delaySec = 2;
    tokenRefreshTimerRef.current = setTimeout(()=>{ loadCopilotToken(); }, delaySec*1000);
  }, [loadCopilotToken]);

  useEffect(()=>{ scheduleCopilotTokenRefresh(copilotTokenInfo); return ()=>{ if(tokenRefreshTimerRef.current){ clearTimeout(tokenRefreshTimerRef.current); tokenRefreshTimerRef.current=null; } }; }, [copilotTokenInfo, scheduleCopilotTokenRefresh]);
  useEffect(()=>{ if(!isLoggedIn && tokenRefreshTimerRef.current){ clearTimeout(tokenRefreshTimerRef.current); tokenRefreshTimerRef.current=null; } }, [isLoggedIn]);
  // Token kalan süre sayaç
  useEffect(()=>{
    if(!copilotTokenInfo?.expires_at){ setTokenCountdown(null); return; }
    const update = () => {
      const remain = copilotTokenInfo.expires_at - nowSeconds();
      setTokenCountdown(remain > 0 ? remain : 0);
    };
    update();
    const iv = setInterval(update, 1000);
    return ()=> clearInterval(iv);
  }, [copilotTokenInfo]);

  // Proxy / models
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(GPT_MODEL_ID); // Varsayılan artık GPT-5
  const [modelsError, setModelsError] = useState(null);

  // Model dropdown seçeneklerini oluşturan fonksiyon (inline IIFE yerine)
  const buildModelOptions = useCallback(() => {
    const base = [
        { label: 'GPT-5.2', value: GPT_MODEL_ID },
        {label: 'GPT-5 mini', value: 'gpt-5-mini'},
        { label: 'GPT-5.1-Codex-Max', value: 'gpt-5.1-codex-max' },
        { label: 'Codex', value: CODEX_MODEL_ID },
        {label: 'Claude Opus 4.5', value: 'claude-opus-4.5'},
        {label: 'Claude Sonnet 4.5', value: 'claude-sonnet-4.5'},
        {label: 'Claude Haiku 4.5', value: 'claude-haiku-4.5'},
        {label: 'Gemini 3 Pro (Preview)', value: 'gemini-3-pro-preview'},
        {label: 'Gemini 3 Flash (Preview)', value: 'gemini-3-flash-preview'},
    ];
    const rest = models.map(m => ({ label: m.name || m.id, value: m.id }));
    return [...base, ...rest];
  }, [models]);

  // Usage
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [showRawUsage, setShowRawUsage] = useState(false);

  // Chat
  const [chatMessages, setChatMessages] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [sending, setSending] = useState(false);
  const [streamingCodex, setStreamingCodex] = useState(false);
  const streamingCancelRef = useRef(null);
  const streamingAssistantIdRef = useRef(null);
  const bottomRef = useRef(null);
  const promptAreaRef = useRef(null);
  const globalCaretRef = useRef({ element: null, type: null, start: 0, end: 0, range: null });

  // PAT (Personal Access Token) flow
  const [showPatDialog, setShowPatDialog] = useState(false);
  const [patValue, setPatValue] = useState('');
  const [patSubmitting, setPatSubmitting] = useState(false);
  const [patError, setPatError] = useState(null);
  // Ayarlar dialog görünürlüğü
  const [showSettings, setShowSettings] = useState(false);

  // Yeni: polling flicker engelleme için ayrı state
  const [isPolling, setIsPolling] = useState(false);

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:'smooth'}); }, [chatMessages]);

  // Mount: auth status kontrolü
  useEffect(()=>{
    (async ()=> {
      try {
        const st = await copilotService.getAuthStatus();
        setIsLoggedIn(!!st.loggedIn);
      } catch { /* yoksay */ }
    })();
    return ()=> { if(pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, []);

  // ---- Device Flow Başlat ----
  const startLogin = async () => {
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    setDeviceFlowError(null);
    setDeviceDialogVisible(true);
    setDeviceCodeData(null);
    setPollAttempts(0);
    try {
      const data = await copilotService.startDeviceCode();
      setDeviceCodeData(data);
      const iv = (data.interval + 1) || 5;
      setPollIntervalSec(iv);
      pollTimerRef.current = setInterval(()=>pollOnce(data.device_code, false), iv*1000);
    } catch (e) {
      setDeviceFlowError(e.message);
    }
  };

  const resetPollingTimer = (device_code, newIntervalSec) => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(()=>pollOnce(device_code, false), newIntervalSec * 1000);
  };

  const pollOnce = async (device_code, _manual = false) => {
    if (!device_code) return;
    // _manual parametresi ileride manuel tetikleme davranışı için saklanmıştır.
    if (pollAttempts >= MAX_POLL_ATTEMPTS) {
      setDeviceFlowError('Maksimum deneme sınırına ulaşıldı. Lütfen tekrar deneyin.');
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      return;
    }
    setPollAttempts(a => a + 1);
    setIsPolling(true);
    try {
      const resp = await copilotService.pollDeviceCode(device_code);
      if (resp.status === 'pending') {
        if (resp.slowDown) {
          setPollIntervalSec(prev => {
            const updated = prev + 2;
            resetPollingTimer(device_code, updated);
            return updated;
          });
        }
        setIsPolling(false);
        return;
      }
      if (resp.status === 'ok') {
        setIsLoggedIn(true);
        GeneralUtils.showNotification({ type: 'SUCCESS', message: 'GitHub girişi başarıyla tamamlandı.' });
        if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
        loadCopilotToken();
        setDeviceDialogVisible(false);
      } else if (resp.status === 'error') {
        setDeviceFlowError(resp.error || 'Bilinmeyen hata');
        if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      }
    } catch (e) {
      setDeviceFlowError(e.message);
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    } finally {
      setIsPolling(false);
    }
  };

  const cancelLogin = () => {
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = null;
    setDeviceDialogVisible(false);
    setDeviceCodeData(null);
    setDeviceFlowError(null);
    setIsPolling(false);
    setPollAttempts(0);
  };

  const logout = async () => {
    await copilotService.logout();
    setIsLoggedIn(false);
    setCopilotTokenInfo(null);
    if(tokenRefreshTimerRef.current){ clearTimeout(tokenRefreshTimerRef.current); tokenRefreshTimerRef.current=null; }
    setUsage(null);
    setChatMessages([]);
    setShowSettings(false);
    GeneralUtils.showNotification({ type:'INFO', message:'Çıkış yapıldı.' });
  };


  // ---- Usage ----
  const loadUsage = async () => {
    setUsageLoading(true);
    try {
      const u = await copilotService.getUsage();
      setUsage(u);
    } catch(e){
      GeneralUtils.showNotification({ type:'ERROR', message:'Usage alınamadı: '+e.message });
    } finally { setUsageLoading(false); }
  };

  // ---- Modeller (simplified according to new index.js) ----
  const loadModels = async () => {
    setModelsLoading(true);
    setModelsError(null);
    try {
      if(!isLoggedIn){
        setModelsError('Önce giriş yapın');
        return;
      }
      let list = [];
      try {
        const direct = await copilotService.getCopilotModelsDirect();
        if (direct && Array.isArray(direct.data)) list = direct.data;
      } catch (e){
        try {
          const fallback = await copilotService.getModels();
          if(fallback && Array.isArray(fallback.data)) list = fallback.data;
          else if(Array.isArray(fallback?.models)) list = fallback.models;
        } catch(fbErr){
          setModelsError(fbErr.message || 'Model yükleme hatası');
        }
      }
      // Duplicate'leri filtrele (id'ye göre), codex varsayılanı hariç
      const existingIds = new Set();
      const cleaned = [];
      for(const m of list){
        if(!m || !m.id) continue;
        if(m.id === CODEX_MODEL_ID) continue; // çakışma engelle
        if(existingIds.has(m.id)) continue;
        existingIds.add(m.id);
        cleaned.push(m);
      }
      setModels(cleaned);
      // Varsayılan seçimi artık GPT-5; kullanıcı isterse Codex'e geçebilir.
    } finally { setModelsLoading(false); }
  };

  // ---- Chat ----
  const sendChat = async () => {
    if(!prompt.trim() || !selectedModel) return;
    if(streamingCodex){
      GeneralUtils.showNotification({ type:'WARN', message:'Devam eden Codex akışı bitmeden yeni istek gönderilemez.' });
      return;
    }
    const userMsg = { id: chatMessages.length+1, role:'user', content: prompt };
    const isCodex = selectedModel === CODEX_MODEL_ID;
    const isNative = !isCodex; // Codex dışındaki her şey native backend /api/copilot/chat
    if(isCodex){
      // Prepare streaming
      const assistantId = userMsg.id + 1;
      streamingAssistantIdRef.current = assistantId;
      setChatMessages(m=>[...m, userMsg, { id: assistantId, role:'assistant', content:'' }]);
      setPrompt('');
      setStreamingCodex(true);
      try {
        const { cancel } = await copilotService.codexStream({
          prompt: userMsg.content,
          onDelta: (piece) => {
            setChatMessages(m => m.map(msg => msg.id === assistantId ? { ...msg, content: msg.content + piece } : msg));
          },
          onDone: () => {
            setStreamingCodex(false);
            streamingCancelRef.current = null;
            streamingAssistantIdRef.current = null;
          },
          onError: (err) => {
            setStreamingCodex(false);
            streamingCancelRef.current = null;
            const em = err?.message || 'Akış hatası';
            setChatMessages(m => m.map(msg => msg.id === assistantId ? { ...msg, content: (msg.content || '') + '\n[HATA: '+em+']' } : msg));
            GeneralUtils.showNotification({ type:'ERROR', message:'Codex akışı hata: '+ em });
            streamingAssistantIdRef.current = null;
          }
        });
        streamingCancelRef.current = cancel;
      } catch(e){
        setStreamingCodex(false);
        streamingCancelRef.current = null;
        streamingAssistantIdRef.current = null;
        GeneralUtils.showNotification({ type:'ERROR', message:'Codex başlatma hatası: '+e.message });
      }
      return;
    }
    // Native Chat (Codex dışı tüm modeller GPT-5 dahil)
    setChatMessages(m=>[...m, userMsg]);
    setPrompt('');
    setSending(true);
    try {
      const resp = await copilotService.chatNative({
        model: selectedModel,
        messages: [...chatMessages, userMsg].map(m=>({ role:m.role, content:m.content })),
        temperature:0.2
      });
      const assistantContent = (resp && resp.content && resp.content.trim()) ? resp.content.trim() : '(Boş yanıt)';
      setChatMessages(m=>[...m, { id: userMsg.id+1, role:'assistant', content: assistantContent }]);
    } catch(e){
      GeneralUtils.showNotification({ type:'ERROR', message:'Chat (native) hatası: '+e.message });
    } finally { setSending(false); }
  };

  const handleKeyDown = (e) => { if(e.key==='Enter' && !e.shiftKey){ e.preventDefault(); sendChat(); } };

  // ---- PAT Login ----
  const closePatDialog = () => {
    if(!patSubmitting) setShowPatDialog(false);
  };
  const submitPat = async () => {
    setPatError(null);
    if(!patValue.trim()){
      setPatError('Token gerekli');
      return;
    }
    setPatSubmitting(true);
    try {
      const resp = await copilotService.patLogin(patValue.trim());
      setIsLoggedIn(true);
      setShowPatDialog(false);
      GeneralUtils.showNotification({ type:'SUCCESS', message:'PAT ile giriş başarılı: '+resp.login });
    } catch(e){
      setPatError(e.message);
    } finally {
      setPatSubmitting(false);
      setPatValue('');
    }
  };

  // ---- UI Helpers ----
  const usageSummary = () => {
    if(!usage) return null;
    const snaps = usage.quota_snapshots || {};
    const entries = Object.entries(snaps);
    if(entries.length === 0) return <p style={{fontSize:12,opacity:.8}}>Kota verisi yok.</p>;
    return <div className='usage-grid'>
      {entries.map(([k,v])=>{
        const ent = v?.entitlement ?? 0;
        const rem = v?.quota_remaining ?? 0;
        const used = ent && rem >=0 ? (ent - rem) : 0;
        const pct = ent ? Math.min(100, Math.round((used/ent)*100)) : 0;
        return (
          <div className='usage-item' key={k}>
            <span className='k'>{k}</span>
            <span className='v'>{used}/{ent} ({pct}%)</span>
            <div className='bar'><div className='fill' style={{width:pct+'%'}} /></div>
          </div>
        );
      })}
    </div>;
  };

  // LOGIN UI (sadəcə buton veya device code aşaması)
  const loginSection = () => {
    // Sadece giriş butonları
    return <>
      <div style={{display: 'flex', justifyContent: 'center', padding: '60px 0'}}>
        <Button type='button' label='GitHub ile Giriş' icon='pi pi-github' className='p-button-sm' onClick={startLogin}/>
      </div>
      {/*<div style={{display: 'flex', justifyContent: 'center', padding: '0 0 40px'}}>*/}
      {/*  <Button label='GitHub PAT ile Giriş' icon='pi pi-github' className='p-button-sm' onClick={() => setShowPatDialog(true)}/>*/}
      {/*  {patSuccessUser && <p style={{fontSize: 12, marginTop: 8}}>PAT kullanıcı: <b>{patSuccessUser}</b></p>}*/}
      {/*</div>*/}
    </>;
  };

  // Ayarlar içeriği (dialog)
  const settingsContent = () => {
    return <div style={{display:'flex',flexDirection:'column',gap:18}}>
      <div>
        <h4 style={{margin:'0 0 6px'}}>Copilot Token</h4>
        <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
          <Button label={copilotLoading? 'Alınıyor...' : (copilotTokenInfo? 'Yenile':'Token Al')} className='p-button-sm' disabled={copilotLoading} onClick={loadCopilotToken} />
          <Button label='Logout' className='p-button-sm p-button-danger' onClick={logout} />
          {copilotTokenInfo && <Button label={showCopilotToken? 'Token Gizle':'Token Göster'} className='p-button-sm p-button-text' onClick={()=>setShowCopilotToken(s=>!s)} />}
        </div>

        {copilotTokenInfo && <div className='token-box' style={{marginTop:10}}>
          {showCopilotToken?
              <code>{JSON.stringify(copilotTokenInfo,null,2)}</code> :
              <div style={{fontSize: 12, lineHeight: 1.4}}>
                <div><b>Tracking ID:</b> {extractTrackingId(copilotTokenInfo.token) || '-'}</div>
                <div><b>Refresh In:</b> {copilotTokenInfo ? (tokenCountdown !== null ? tokenCountdown + 's' : '-') : '-'}</div>
                <div><b>Kalan Süre:</b> {tokenCountdown !== null ? tokenCountdown + 's' : '-'}</div>

                <div><b>Login:</b> {isLoggedIn ? 'Evet' : 'Hayır'}</div>
                <div><b>Copilot Token:</b> {copilotTokenInfo ? 'Var' : 'Yok'}</div>
                <div><b>Model Sayısı:</b> {models.length + 1 /* codex dahil */}</div>
                <div><b>Model (seçili):</b> {selectedModel || '-'}</div>

                <div style={{opacity: .7}}>Token gizli. "Göster" ile JSON görüntülenir.</div>
              </div>
          }
        </div>}
      </div>
      <Divider/>
      <div>
      <h4 style={{margin: '0 0 6px'}}>Kullanım / Kota</h4>
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
          <Button label='Yenile' onClick={loadUsage} loading={usageLoading} disabled={!isLoggedIn}
                  className='p-button-sm p-button-text'/>
          {usage && <Button label={showRawUsage ? 'Özet' : 'Ham JSON'} className='p-button-sm p-button-text'
                            onClick={() => setShowRawUsage(v => !v)}/>}
        </div>
        {!usage && <p style={{fontSize:12,opacity:.8}}>Henüz yüklenmedi.</p>}
        {usage && !showRawUsage && usageSummary()}
        {usage && showRawUsage && <pre className='json-view' style={{maxHeight:240,overflow:'auto'}}>{JSON.stringify(usage,null,2)}</pre>}
      </div>
    </div>;
  };

  // --- Clipboard Helpers (loginSection öncesine taşındı) ---
  const copyUserCode = () => {
    if(!deviceCodeData?.user_code) return;
    navigator.clipboard.writeText(deviceCodeData.user_code)
      .then(()=> GeneralUtils.showNotification({type:'SUCCESS', message:'Kod kopyalandı.'}))
      .catch(e=> GeneralUtils.showNotification({type:'ERROR', message:'Kopyalama başarısız: '+ e.message}));
  };
  const copyCodeAndOpen = () => {
    copyUserCode();
    if(deviceCodeData?.verification_uri){
      try { window.open(deviceCodeData.verification_uri, '_blank','noopener'); } catch(_){ /* ignore */ }
    }
  }

  const cancelCodexStream = useCallback(() => {
    if(streamingCodex && streamingCancelRef.current){
      try { streamingCancelRef.current(); } catch(_) { /* ignore */ }
      setStreamingCodex(false);
      const id = streamingAssistantIdRef.current;
      if(id){
        setChatMessages(m => m.map(msg => msg.id === id ? { ...msg, content: msg.content + '\n[İptal edildi]' } : msg));
      }
      streamingAssistantIdRef.current = null;
      streamingCancelRef.current = null;
    }
  }, [streamingCodex]);

  // Assistant message actions
  const copyAssistantMessage = useCallback((text) => {
    if(!text) return;
    if(!navigator?.clipboard){
      try {
        const ta = document.createElement('textarea');
        ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); document.body.removeChild(ta);
        GeneralUtils.showNotification({type:'SUCCESS', message:'Mesaj kopyalandı'});
      } catch(e){
        GeneralUtils.showNotification({type:'ERROR', message:'Kopyalama desteklenmiyor'});
      }
      return;
    }
    navigator.clipboard.writeText(text)
      .then(()=> GeneralUtils.showNotification({type:'SUCCESS', message:'Mesaj kopyalandı'}))
      .catch(err=> GeneralUtils.showNotification({type:'ERROR', message:'Kopyalama başarısız: '+ err.message}));
  }, []);

  // Global caret tracking
  useEffect(()=>{
    const isEditableInput = (el) => el && (el.tagName === 'TEXTAREA' || (el.tagName === 'INPUT' && !['button','submit','checkbox','radio','file','reset','image'].includes(el.type))) && !el.readOnly && !el.disabled;

    const updateFromActive = () => {
      const ae = document.activeElement;
      if(isEditableInput(ae)) {
        try {
          globalCaretRef.current = {
            element: ae,
            type: 'input',
            start: ae.selectionStart ?? 0,
            end: ae.selectionEnd ?? 0,
            range: null
          };
        } catch { /* ignore */ }
        return;
      }
      if(ae && ae.isContentEditable) {
        const sel = window.getSelection();
        if(sel && sel.rangeCount > 0) {
          const r = sel.getRangeAt(0).cloneRange();
          globalCaretRef.current = { element: ae, type: 'contenteditable', start: 0, end: 0, range: r };
          return;
        }
      }
    };

    const onSelectionChange = () => {
      const ae = document.activeElement;
      if(!ae) return;
      if(ae === promptAreaRef.current) { // internal prompt; we still track but mark as prompt
        try {
          globalCaretRef.current = {
            element: ae,
            type: 'prompt',
            start: ae.selectionStart ?? 0,
            end: ae.selectionEnd ?? 0,
            range: null
          };
        } catch { /* ignore */ }
        return;
      }
      updateFromActive();
    };

    document.addEventListener('selectionchange', onSelectionChange, true);
    document.addEventListener('focusin', onSelectionChange, true);
    document.addEventListener('keyup', onSelectionChange, true);
    document.addEventListener('mouseup', onSelectionChange, true);
    return ()=>{
      document.removeEventListener('selectionchange', onSelectionChange, true);
      document.removeEventListener('focusin', onSelectionChange, true);
      document.removeEventListener('keyup', onSelectionChange, true);
      document.removeEventListener('mouseup', onSelectionChange, true);
    };
  }, []);

  const insertIntoPromptAtCursor = useCallback((text) => {
    if(!text) return;
    const caretInfo = globalCaretRef.current;
    const isValidEl = (el) => el && document.contains(el);

    const insertIntoInput = (el) => {
      try {
        const start = el.selectionStart ?? caretInfo.start ?? 0;
        const end = el.selectionEnd ?? caretInfo.end ?? start;
        const value = el.value ?? '';
        const before = value.slice(0,start);
        const after = value.slice(end);
        const updated = before + text + after;
        el.value = updated;
        const newPos = start + text.length;
        el.focus();
        try { el.selectionStart = el.selectionEnd = newPos; } catch {}
        // trigger input event to inform frameworks
        const evt = new Event('input', { bubbles:true });
        el.dispatchEvent(evt);
      } catch { /* ignore */ }
    };

    const insertIntoContentEditable = (el) => {
      const sel = window.getSelection();
      if(!sel) return;
      if(caretInfo.range) {
        try {
          sel.removeAllRanges();
          sel.addRange(caretInfo.range);
        } catch { /* ignore */ }
      }
      if(sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      // ensure range inside element
      if(!el.contains(range.startContainer)) return insertIntoPromptAtCursor(text); // fallback recursion -> will eventually go to prompt
      range.deleteContents();
      const node = document.createTextNode(text);
      range.insertNode(node);
      // move caret after inserted node
      range.setStartAfter(node);
      range.setEndAfter(node);
      sel.removeAllRanges();
      sel.addRange(range);
    };

    // Prefer external editable if not prompt
    if(isValidEl(caretInfo.element) && caretInfo.type && caretInfo.type !== 'prompt') {
      if(caretInfo.type === 'input') {
        insertIntoInput(caretInfo.element);
        return;
      }
      if(caretInfo.type === 'contenteditable') {
        insertIntoContentEditable(caretInfo.element);
        return;
      }
    }

    // Fallback: internal prompt
    const promptEl = promptAreaRef.current;
    if(promptEl) {
      try {
        const start = promptEl.selectionStart ?? 0;
        const end = promptEl.selectionEnd ?? start;
        const value = promptEl.value ?? '';
        const before = value.slice(0,start);
        const after = value.slice(end);
        const updated = before + text + after;
        promptEl.value = updated;
        const newPos = start + text.length;
        promptEl.focus();
        promptEl.selectionStart = promptEl.selectionEnd = newPos;
        const evt = new Event('input', { bubbles:true });
        promptEl.dispatchEvent(evt);
        setPrompt(updated); // sync react state
      } catch { /* ignore */ }
    } else {
      // ultimate fallback append to state
      setPrompt(p => p ? (p + (p.endsWith('\n') ? '' : '\n') + text) : text);
    }
  }, [setPrompt]);

  // RETURN updated: floating fab + conditional panel
  return (
    <>
      {/* Floating GitHub FAB */}
      <button type='button' className='copilot-fab' aria-label='GitHub Copilot Paneli' onClick={togglePanel} title='GitHub Copilot Paneli'>
        <i className='pi pi-bolt' style={{fontSize:20}}/>
      </button>

      {/* Panel */}
      {panelOpen && (
        <div className={`copilot-floating-panel ${panelClosing? 'closing':''}`} style={{width: panelWidth}}>
          <div className='copilot-resizer' onMouseDown={startResize} title='Genişliği ayarla' />
          <div className='copilot-panel-header'>
            <div className='panel-header-main'>
              <div className='left'>
                <i className='pi pi-bolt' /> <span>Yapay Zeka Asistanı</span>
                {isLoggedIn && <span style={{fontSize:10, background:'#1e293b', padding:'2px 6px', borderRadius:4}}>AI</span>}
              </div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <button onClick={togglePanel} title='Kapat' aria-label='Paneli kapat'><i className='pi pi-times'/></button>
              </div>
            </div>
            {isLoggedIn && (
              <div className='copilot-chat-toolbar'>
                <h6><i className='pi pi-comments'/><span>Sohbet</span></h6>
                <div className='toolbar-actions'>
                  <Button icon='pi pi-cog' className='p-button-rounded p-button-text' onClick={()=>setShowSettings(true)} tooltip='Ayarlar' />
                  <Button icon='pi pi-sign-out' className='p-button-rounded p-button-text p-button-danger' onClick={logout} tooltip='Çıkış' />
                </div>
              </div>
            )}
          </div>
          <div className={`copilot-floating-body ${isLoggedIn ? 'logged-in':''}`}>
            <div className={`copilot-wrapper ${isLoggedIn ? 'logged-in':''}`}>
              {/* ORIGINAL RETURN BODY START */}
              {!isLoggedIn && (
                <>
                  {loginSection()}
                  {/* Device Code Dialogs kept but displayed inline while floating panel open */}
                  <Dialog header='GitHub Device Code' visible={deviceDialogVisible} style={{width:'560px'}} modal closable onHide={cancelLogin}>
                    {!deviceCodeData && !deviceFlowError && <p style={{textAlign:'center',margin:0}}><i className='pi pi-spin pi-spinner'/> Kod alınıyor...</p>}
                    {deviceFlowError && (
                        <div style={{textAlign:'center'}}>
                          <p style={{color:'#dc2626',marginTop:0}}>{deviceFlowError}</p>
                          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
                            <Button label='Yeniden Dene' className='p-button-sm' onClick={startLogin} />
                            <Button label='Kapat' className='p-button-text p-button-sm' onClick={cancelLogin} />
                          </div>
                        </div>
                    )}
                    {deviceCodeData && !deviceFlowError && <>
                      <p style={{marginTop:0}}>Tarayıcıda <a href={deviceCodeData.verification_uri} target='_blank' rel='noreferrer'>{deviceCodeData.verification_uri}</a> adresini açıp aşağıdaki kodu girin.</p>
                      <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                        <code style={{fontSize:20,fontWeight:600,padding:'4px 10px',background:'#1e293b',color:'#fff',borderRadius:6}}>{deviceCodeData.user_code}</code>
                        <Button type='button' icon='pi pi-copy' className='p-button-sm' onClick={copyUserCode} tooltip='Kodu kopyala' />
                        <Button type='button' label='Kopyala & Aç' className='p-button-sm p-button-success' onClick={copyCodeAndOpen} />
                      </div>
                      <Divider />
                      <p style={{fontSize:12, opacity:.85, marginTop:0}}>Otomatik kontrol ediliyor (interval: {pollIntervalSec}s | deneme: {pollAttempts}/{MAX_POLL_ATTEMPTS}). {isPolling && <span style={{marginLeft:6}}><i className='pi pi-spin pi-spinner'/> kontrol...</span>}</p>
                      <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                        <Button type='button' label='Manuel Kontrol' className='p-button-sm p-button-secondary' onClick={()=>pollOnce(deviceCodeData.device_code, true)} />
                        <Button type='button' label='İptal' className='p-button-sm' onClick={cancelLogin} />
                      </div>
                    </>}
                  </Dialog>
                  <Dialog header='PAT ile Giriş' visible={showPatDialog} style={{width:'420px'}} modal closable={!patSubmitting} onHide={closePatDialog}>
                    <div style={{display:'flex', flexDirection:'column', gap:12}}>
                      <p style={{fontSize:12, margin:0}}>GitHub klasik Personal Access Token giriniz. Sadece <code>read:user</code> scope yeterlidir.</p>
                      <InputText value={patValue} type='password' placeholder='ghp_...' onChange={e=>setPatValue(e.target.value)} disabled={patSubmitting} className='p-inputtext-sm'/>
                      {patError && <div style={{color:'#b91c1c', fontSize:12}}>{patError}</div>}
                      <div style={{display:'flex', justifyContent:'flex-end', gap:8}}>
                        <Button type='button' label='Vazgeç' className='p-button-text p-button-sm' onClick={closePatDialog} disabled={patSubmitting}/>
                        <Button type='button' label={patSubmitting? 'Gönderiliyor...' : 'Giriş'} icon='pi pi-check' className='p-button-sm' onClick={submitPat} disabled={patSubmitting}/>
                      </div>
                    </div>
                  </Dialog>
                </>
              )}
              {isLoggedIn && (
                <>
                  <div className='chat-card chat-full' style={{background:'transparent'}}>
                    <div className='chat-messages'>
                      {chatMessages.map(m => (
                        <div key={m.id} className={m.role === 'user' ? 'msg user' : 'msg assistant'}>
                          {m.role === 'assistant' ? <span className='avatar'><i className='pi pi-bolt' style={{fontSize: 16}}/></span> : <Badge value='Ben'/>}
                          <div className='content'>
                            {m.role === 'assistant' && m.content && m.content.trim().length > 0 && (
                              <div className='assistant-actions'>
                                <button type='button' onMouseDown={(e)=>e.preventDefault()} onClick={()=>copyAssistantMessage(m.content)} title='Kopyala' aria-label='Kopyala'>
                                  <i className='pi pi-copy'/>
                                </button>
                                <button type='button' onMouseDown={(e)=>e.preventDefault()} onClick={()=>insertIntoPromptAtCursor(m.content)} title='İmlece ekle' aria-label='İmlece ekle'>
                                  <i className='pi pi-plus'/>
                                </button>
                              </div>
                            )}
                            {m.content}
                          </div>
                        </div>
                      ))}
                      {streamingCodex ? (
                        <div className='msg assistant streaming-indicator'>
                          <span className='avatar'><i className='pi pi-bolt' style={{fontSize: 16}}/></span>
                          <div className='content'><i className='pi pi-spin pi-spinner'/> Akış...</div>
                        </div>
                      ) : (sending && selectedModel !== CODEX_MODEL_ID) ? (
                        <div className='msg assistant'>
                          <span className='avatar'><i className='pi pi-bolt' style={{fontSize: 16}}/></span>
                          <div className='content'><i className='pi pi-spin pi-spinner'/> Yanıt bekleniyor...</div>
                        </div>
                      ) : null}
                      <div ref={bottomRef}/>
                    </div>
                  </div>
                  <Dialog header='Ayarlar' visible={showSettings} style={{width: '620px'}} modal onHide={() => setShowSettings(false)}>
                    {settingsContent()}
                  </Dialog>
                </>
              )}
            </div>
          </div>
          {isLoggedIn && (
            <div className='copilot-bottom-input'>
              <div className='prompt-area'>
                <InputTextarea
                  ref={promptAreaRef}
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedModel === CODEX_MODEL_ID ? 'Codex prompt yazın ve Enter' : (selectedModel === GPT_MODEL_ID ? 'GPT-5 ile sohbet edin ve Enter' : 'Mesajınızı yazın ve Enter\'a basın')}
                  className='w-full'
                  disabled={sending || streamingCodex}
                />
              </div>
              <div className='chat-controls'>
                <Dropdown value={selectedModel} options={buildModelOptions()} onChange={e => setSelectedModel(e.value)} className='p-inputtext-sm' placeholder='Model seçin' style={{minWidth: 240}}/>
                {/*<Button type='button' icon='pi pi-refresh' tooltip='Modelleri Yükle' className='p-button-sm p-button-text' onClick={loadModels} loading={modelsLoading} disabled={streamingCodex}/>*/}
                {/*{modelsError && <span style={{fontSize: 11, color: '#b91c1c'}}>Hata: {modelsError}</span>}*/}
                <div style={{display: 'flex', gap: 8, marginLeft: 'auto', alignItems: 'center'}}>
                  {streamingCodex ? (
                    <Button type='button' label='Durdur' icon='pi pi-stop' className='p-button-sm p-button-danger' onClick={cancelCodexStream}/>
                  ) : (
                    <Button icon='pi pi-send' disabled={!prompt.trim() || sending} onClick={sendChat} className='p-button-sm'/>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Copilot;
